import { useMemo } from 'react';
import { Dropdown, FilterableMultiSelect } from '@carbon/react';
import { decisionFiltersAtom, EMPTY_FILTERS } from './atoms';
import {
  useAutoMatchConfig,
  useDecisionCounties,
  useReasonLabels,
} from './hooks';
import {
  LLM_REASON_LABEL,
  OUTCOME_LABEL,
  SUPPRESSION_REASON_LABEL,
} from './utils';
import type { CountyOption } from './types';
import { useLogFilters } from '../hooks';
import {
  LogDateRangeFilter,
  LogResetButton,
  LogSearchFilter,
} from '../log-filters';
import shared from '../admin-log.module.scss';

const EMPTY_COUNTIES: CountyOption[] = [];

// Reversal is its own axis, not an outcome: an undone row is still an
// auto-confirm, it is just one the examiner took back.
const REVERSAL_ITEMS = [
  { id: '', label: 'All decisions' },
  { id: 'undone', label: 'Undone by examiner' },
  { id: 'standing', label: 'Still standing' },
];

const OUTCOME_ITEMS = [
  { id: '', label: 'All outcomes' },
  { id: 'auto_confirmed', label: OUTCOME_LABEL.auto_confirmed },
  { id: 'manual_review', label: OUTCOME_LABEL.manual_review },
];

// Reasons that are not guardrails, so the roster has nothing to say about them,
// in the order the gate evaluates them, before the guardrails.
const LEADING_REASON_ITEMS = [
  { id: '', label: 'All reasons' },
  { id: 'LLM', label: LLM_REASON_LABEL.LLM },
  { id: 'Threshold', label: 'Threshold' },
];

/**
 * The Basis / reason filter's options: Threshold, then the guardrails from the
 * config's roster, then the prose reasons — labelled from the same map the
 * column and the detail use.
 * @return {{ id: string, label: string }[]} The options
 */
const useReasonItems = () => {
  const { data: config } = useAutoMatchConfig();
  const labels = useReasonLabels();

  return useMemo(() => {
    const ids = [
      ...(config?.availableGuardrails ?? []).map((guardrail) => guardrail.id),
      ...Object.keys(SUPPRESSION_REASON_LABEL),
    ];
    return [
      ...LEADING_REASON_ITEMS,
      ...ids.map((id) => ({ id, label: labels[id] ?? id })),
    ];
  }, [config, labels]);
};

/**
 * The decision log's filter bar. Any change returns to the first page.
 * @return {JSX.Element} The filter bar
 */
export function DecisionFilters() {
  const { filters, patchFilter, resetKey, reset } = useLogFilters(
    decisionFiltersAtom,
    EMPTY_FILTERS
  );
  const reasonItems = useReasonItems();
  // County options — the counties represented in the log, not the full US list —
  // are fetched once by their own query, so they stay stable across the list's
  // refetches rather than riding (and blinking with) the paginated payload.
  // A failed fetch shows on the filter itself, so an empty list reads as "couldn't
  // load" rather than "no counties" (the list has its own error notification).
  const { data: countyItems = EMPTY_COUNTIES, isError: countiesFailed } =
    useDecisionCounties();

  return (
    <div className={shared.filterBar}>
      <Dropdown
        id="decision-outcome"
        className={shared.filterField}
        titleText="Outcome"
        label="All outcomes"
        items={OUTCOME_ITEMS}
        itemToString={(item) => item?.label ?? ''}
        selectedItem={
          OUTCOME_ITEMS.find((i) => i.id === filters.outcome) ??
          OUTCOME_ITEMS[0]
        }
        onChange={({ selectedItem }) =>
          patchFilter({ outcome: selectedItem?.id ?? '' })
        }
      />
      <Dropdown
        id="decision-reversal"
        className={shared.filterField}
        titleText="Reversal"
        label="All decisions"
        items={REVERSAL_ITEMS}
        itemToString={(item) => item?.label ?? ''}
        selectedItem={
          REVERSAL_ITEMS.find((i) => i.id === filters.reversal) ??
          REVERSAL_ITEMS[0]
        }
        onChange={({ selectedItem }) =>
          patchFilter({ reversal: selectedItem?.id ?? '' })
        }
      />
      <Dropdown
        id="decision-reason"
        className={shared.filterField}
        titleText="Basis / reason"
        label="All reasons"
        items={reasonItems}
        itemToString={(item) => item?.label ?? ''}
        selectedItem={
          reasonItems.find((i) => i.id === filters.suppressionReason) ??
          reasonItems[0]
        }
        onChange={({ selectedItem }) =>
          patchFilter({ suppressionReason: selectedItem?.id ?? '' })
        }
      />
      <FilterableMultiSelect
        // Controlled by the filter atom so it stays selected across picks
        // (the menu does not remount) and clears on Reset.
        id="decision-county"
        className={shared.filterField}
        titleText="County"
        placeholder="All counties"
        invalid={countiesFailed}
        invalidText="Couldn't load counties — refresh to try again."
        items={countyItems}
        selectedItems={countyItems.filter((c) =>
          filters.countyIds.includes(c.id)
        )}
        itemToString={(item: CountyOption | null) => {
          if (!item) return '';
          return `${item.name}, ${item.stateAbbreviation}`;
        }}
        onChange={({
          selectedItems,
        }: {
          selectedItems: readonly CountyOption[];
        }) => patchFilter({ countyIds: selectedItems.map((c) => c.id) })}
        selectionFeedback="fixed"
      />
      <LogDateRangeFilter
        key={`range-${resetKey}`}
        idPrefix="decision"
        after={filters.after}
        before={filters.before}
        onChange={patchFilter}
      />
      <LogSearchFilter
        key={`search-${resetKey}`}
        id="decision-search"
        labelText="Search (document, staged or matched entity id)"
        placeholder="e.g. 628"
        value={filters.search}
        onChange={(search) => patchFilter({ search })}
      />
      <LogResetButton onReset={reset} />
    </div>
  );
}
